import { resolveInsurableGroupLabel } from '@/features/quotes/data/group-life/insurable-groups';
import type { GroupLifeTarifacaoLife } from '@/services/group-life';
import { formatCpf, formatCurrency } from '@/shared/utils';
import { Dialog, Text } from '@noorden-insurtech/alba-design-system';
import { User } from 'lucide-react';
import { formatLifeBirthDate } from './pme-policy-lives-columns';

const DASH = '—';

function genderLabel(gender?: string): string {
  if (gender === 'M') return 'Masculino';
  if (gender === 'F') return 'Feminino';
  return DASH;
}

function money(value?: number | null): string {
  return value == null ? DASH : formatCurrency(value);
}

/**
 * CPF da vida em `XXX.XXX.XXX-XX`. O `formatCpf` só aplica a máscara com 11
 * dígitos e devolve o valor cru nos demais casos — importante porque o InsureMO
 * às vezes devolve o CPF MASCARADO por PII (ex.: `390533*4705`), e completar
 * zeros ali fabricaria outro documento.
 */
function cpfLabel(cpf?: string | null): string {
  const value = (cpf ?? '').trim();
  return value ? formatCpf(value) : DASH;
}

function rate(value?: number | null): string {
  if (value == null) return DASH;
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 5,
    maximumFractionDigits: 5,
  });
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex flex-col gap-1'>
      <Text className='text-sm font-bold text-alba-black-500'>{label}</Text>
      <Text className='text-sm text-alba-gray-600'>{value}</Text>
    </div>
  );
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  life: GroupLifeTarifacaoLife | null;
};

/**
 * Detalhe da vida aberto pelo chevron da tabela.
 *
 * O `DataTable` do design system não tem sub-linha expansível, então o
 * conteúdo da expansão vive num modal. Mostra também as coberturas da vida
 * (`coverages[]`), que só existem na tarifação — é a informação que justifica
 * a expansão.
 */
export function PmePolicyLifeDetailModal({ isOpen, onClose, life }: Props) {
  const coverages = life?.coverages ?? [];

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details: { open: boolean }) => {
        if (!details.open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content className='w-full max-w-2xl'>
            <Dialog.Header>
              <Dialog.Title>
                <div className='flex items-center gap-2'>
                  <User className='h-5 w-5 text-alba-black-500' />
                  Detalhes da Vida
                </div>
              </Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <Dialog.CloseButton onClick={onClose} />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <DetailField label='Nome' value={life?.name || DASH} />
                <DetailField label='CPF' value={cpfLabel(life?.cpf)} />
                <DetailField
                  label='Data de Nascimento'
                  value={formatLifeBirthDate(life?.birthDate)}
                />
                <DetailField label='Sexo' value={genderLabel(life?.gender)} />
                <DetailField
                  label='Grupo Segurável'
                  value={
                    life?.insurableGroup
                      ? resolveInsurableGroupLabel(life.insurableGroup)
                      : DASH
                  }
                />
                <DetailField label='Capital' value={life?.capital || DASH} />
                <DetailField
                  label='Prêmio'
                  value={money(life?.totalDuePremium)}
                />
                <DetailField label='IOF' value={money(life?.totalIof)} />
              </div>

              <div className='mt-8 flex flex-col gap-4'>
                <Text className='text-base text-alba-black-500'>
                  Coberturas da Vida
                </Text>

                {coverages.length === 0 ? (
                  <Text className='text-sm text-alba-gray-600'>
                    Esta vida herda as coberturas do grupo segurável.
                  </Text>
                ) : (
                  <div className='overflow-x-auto'>
                    <div className='min-w-[560px] rounded-xl border border-alba-gray-300'>
                      <div className='grid grid-cols-[2fr_1fr_1fr_1fr] gap-4 border-b border-alba-gray-300 px-4 py-3'>
                        <Text className='text-xs font-bold text-alba-black-500'>
                          Cobertura
                        </Text>
                        <Text className='text-right text-xs font-bold text-alba-black-500'>
                          Capital
                        </Text>
                        <Text className='text-right text-xs font-bold text-alba-black-500'>
                          Taxa
                        </Text>
                        <Text className='text-right text-xs font-bold text-alba-black-500'>
                          Prêmio
                        </Text>
                      </div>
                      {coverages.map(coverage => (
                        <div
                          key={coverage.code}
                          className='grid grid-cols-[2fr_1fr_1fr_1fr] gap-4 px-4 py-2'
                        >
                          <Text className='text-sm text-alba-gray-600'>
                            {coverage.name || coverage.code}
                          </Text>
                          <Text className='text-right text-sm text-alba-gray-600'>
                            {money(coverage.sumInsured)}
                          </Text>
                          <Text className='text-right text-sm text-alba-gray-600'>
                            {rate(coverage.pureRate)}
                          </Text>
                          <Text className='text-right text-sm text-alba-gray-600'>
                            {money(coverage.duePremium)}
                          </Text>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
